// @flow
import api, { sleep } from '../../configs/api-generators.js';
import { setProgress, setMessages, setModal } from '../ui/ui.actions.js';
import Organization from '../organization/organization.model.js';

import {
  types,
  GET_TRANSACTIONS,
  BUY_TRANSACTION,
  EDIT_TRANSACTION,
  MODAL_EDIT_CREDIT,
  MODAL_CREDIT,
  ADD_CREDIT,
  SAVE_CREDIT,
  DELETE_CREDIT,
  ADD_OFFLINE
} from './transaction.const.js';

import { payerSelector } from '../user/user.selectors.js';
import { invoiceSelector } from '../invoice/invoice.selectors.js';

import { organizationSelector } from '../organization/organization.selectors.js';

import { extractMessages } from '../ui/ui.helpers.js';

export function* getTransactions(filterParams: string): Yield<*> {
  try {
    yield setProgress(true, GET_TRANSACTIONS);

    const state = yield s => s;
    const payer = payerSelector(state);
    const org = organizationSelector(state);

    const response = yield* api(
      Organization.orgAccess
        ? `/org/${org.unique}/transactions/getAll/${payer.id}/${filterParams}/`
        : `/transactions/getAll/${filterParams}/`,
      {}
    );
    if (response.ok) {
      yield {
        type: GET_TRANSACTIONS,
        payload: {
          ...response.data,
          transactionsList: Object.keys(response.data.transactions)
        }
      };

      yield setMessages(extractMessages(response));
      return setProgress(false, GET_TRANSACTIONS);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, GET_TRANSACTIONS);
  }
}

export function* buyTransaction(payload: { cardId: string, amount: string }): Yield<*> {
  try {
    yield setProgress(true, BUY_TRANSACTION);

    const state = yield s => s;
    const org = organizationSelector(state);

    const invoice = invoiceSelector(state);
    const payer = payerSelector(state);

    const response = yield* api(
      Organization.orgAccess ? `/org/${org.unique}/transactions/buy/` : '/transactions/buy/',
      {
        ...payload,
        organizationId: invoice.organizationId,
        userId: payer.id
      }
    );

    if (response.ok) {
      yield {
        type: BUY_TRANSACTION,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      yield setProgress(false, BUY_TRANSACTION);
      yield sleep(500);

      return setModal(false, BUY_TRANSACTION);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, BUY_TRANSACTION);
  }
}

export function* addCredit(payload: { productId: string, amount: string, id: string }): Yield<*> {
  try {
    yield setProgress(true, ADD_CREDIT);

    const state = yield s => s;
    const org = organizationSelector(state);
    const payer = payerSelector(state);

    const response = yield* api(`/org/${org.unique}/transactions/add/`, {
      productId: payload.productId,
      amount: payload.amount,
      userId: payer.id,
      type: types.credit
    });

    if (response.ok) {
      yield {
        type: ADD_CREDIT,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      yield setProgress(false, ADD_CREDIT);
      yield sleep(500);

      return setModal(false, MODAL_CREDIT);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, ADD_CREDIT);
  }
}

export function* saveCredit(payload: { productId: string, amount: string, id: string }): Yield<*> {
  try {
    yield setProgress(true, SAVE_CREDIT);

    const state = yield s => s;
    const org = organizationSelector(state);

    const response = yield* api(`/org/${org.unique}/transactions/save/${payload.id}/`, {
      productId: payload.productId,
      amount: payload.amount
    });

    if (response.ok) {
      yield {
        type: SAVE_CREDIT,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      yield setProgress(false, SAVE_CREDIT);
      yield sleep(500);

      return setModal(false, MODAL_EDIT_CREDIT);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, SAVE_CREDIT);
  }
}

export function* deleteCredit(id: string): Yield<*> {
  try {
    yield setProgress(true, DELETE_CREDIT);

    const state = yield s => s;
    const org = organizationSelector(state);

    const response = yield* api(`/org/${org.unique}/transactions/delete/${id}/`, {});

    if (response.ok) {
      yield {
        type: DELETE_CREDIT,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      return setProgress(false, DELETE_CREDIT);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, DELETE_CREDIT);
  }
}

export function* refundTransaction({ id, amount }: { id: string, amount: string }): Yield<*> {
  try {
    yield setProgress(true, EDIT_TRANSACTION);

    const state = yield s => s;
    const org = organizationSelector(state);

    const response = yield* api(
      Organization.orgAccess
        ? `/org/${org.unique}/transactions/refund/${id}/`
        : `/transactions/refund/${id}/`,
      { amount }
    );

    if (response.ok) {
      yield {
        type: EDIT_TRANSACTION,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      yield setProgress(false, EDIT_TRANSACTION);
      yield sleep(500);

      return setModal(false, EDIT_TRANSACTION);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, EDIT_TRANSACTION);
  }
}

export function* saveTransaction({ id, amount }: { id: string, amount: string }): Yield<*> {
  try {
    yield setProgress(true, EDIT_TRANSACTION);

    const state = yield s => s;
    const org = organizationSelector(state);

    const response = yield* api(
      Organization.orgAccess
        ? `/org/${org.unique}/transactions/save/${id}/`
        : `/transactions/save/${id}/`,
      { amount }
    );

    if (response.ok) {
      yield {
        type: EDIT_TRANSACTION,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      yield setProgress(false, EDIT_TRANSACTION);
      yield sleep(500);

      return setModal(false, EDIT_TRANSACTION);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, EDIT_TRANSACTION);
  }
}

export function* deleteTransaction(id: string): Yield<*> {
  try {
    yield setProgress(true, EDIT_TRANSACTION);

    const state = yield s => s;
    const org = organizationSelector(state);

    const response = yield* api(`/org/${org.unique}/transactions/delete/${id}/`, {});

    if (response.ok) {
      yield {
        type: EDIT_TRANSACTION,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      yield setProgress(false, EDIT_TRANSACTION);
      yield sleep(500);

      return setModal(false, EDIT_TRANSACTION);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, EDIT_TRANSACTION);
  }
}

export function* addOffline(payload: { type: string, amount: string }): Yield<*> {
  try {
    yield setProgress(true, ADD_OFFLINE);

    const state = yield s => s;
    const org = organizationSelector(state);
    const payer = payerSelector(state);

    const response = yield* api(`/org/${org.unique}/transactions/add/`, {
      amount: payload.amount,
      userId: payer.id,
      type: payload.type
    });

    if (response.ok) {
      yield {
        type: ADD_OFFLINE,
        payload: response.data
      };

      yield setMessages(extractMessages(response));
      return setProgress(false, ADD_OFFLINE);
    }

    throw new Error(
      response && response.error && response.error.length
        ? response.error.join('\n')
        : 'Internal Server Error'
    );
  } catch (e) {
    yield setMessages({
      type: 'error',
      text: e.message
    });
    return setProgress(false, ADD_OFFLINE);
  }
}
